'use client';

import React, { useState } from 'react';
import { Heart, Star, Quote, ChevronDown, ChevronUp } from 'lucide-react';
import { Title } from '@/components/ui/Headings';

interface Review {
  id: number;
  travelerName: string;
  travelerLocation: string | null;
  biggestImpression: string;
  favoriteMoment: string | null;
  wouldRecommend: string | null;
  highlightExperience: string | null;
  additionalComments: string | null;
  travelCompanion: string | null;
  travelMonth: string | null;
  createdAt: Date;
}

interface JourneyReviewsSectionProps {
  journeyTitle: string;
  reviews: Review[];
}

const JourneyReviewsSection: React.FC<JourneyReviewsSectionProps> = ({
  reviews
}) => {
  const [showAllReviews, setShowAllReviews] = useState(false);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long' 
    });
  };

  const getCompanionText = (companion?: string) => {
    switch (companion) {
      case 'solo': return 'Solo traveler';
      case 'couple': return 'Traveled as a couple';
      case 'family': return 'Traveled with family';
      case 'friends': return 'Traveled with friends';
      case 'group': return 'Group tour';
      default: return '';
    }
  };

  const displayedReviews = showAllReviews ? reviews : reviews.slice(0, 3);

  if (reviews.length === 0) {
    return null;
  }

  return (
    <section className="py-12 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <Title size="sm" className="font-semibold mb-4">
            Traveler Experiences
          </Title>
          <p className="text-gray-600 mb-6">
            Discover what made the biggest impression on fellow travelers
          </p>
        </div>

        {/* Reviews Grid */}
        <div className="space-y-6">
          {displayedReviews.map((review) => (
            <div key={review.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              {/* Review Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <Heart className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{review.travelerName}</h4>
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      {review.travelerLocation && (
                        <span>{review.travelerLocation}</span>
                      )}
                      {review.travelMonth && (
                        <span>• {review.travelMonth}</span>
                      )}
                      {review.travelCompanion && (
                        <span>• {getCompanionText(review.travelCompanion)}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  {formatDate(review.createdAt)}
                </div>
              </div>

              {/* Review Content */}
              <div className="space-y-4">
                {/* Biggest Impression */}
                <div>
                  <div className="flex items-center mb-2">
                    <Heart className="h-4 w-4 text-red-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Biggest Impression</span>
                  </div>
                  <p className="text-gray-800 leading-relaxed">{review.biggestImpression}</p>
                </div>

                {/* Favorite Moment */}
                {review.favoriteMoment && (
                  <div>
                    <div className="flex items-center mb-2">
                      <Star className="h-4 w-4 text-yellow-500 mr-2" />
                      <span className="text-sm font-medium text-gray-700">Favorite Moment</span>
                    </div>
                    <p className="text-gray-800 leading-relaxed">{review.favoriteMoment}</p>
                  </div>
                )}

                {/* Would Recommend */}
                {review.wouldRecommend && (
                  <div>
                    <div className="flex items-center mb-2">
                      <Quote className="h-4 w-4 text-blue-500 mr-2" />
                      <span className="text-sm font-medium text-gray-700">Would Tell a Friend</span>
                    </div>
                    <p className="text-gray-800 leading-relaxed italic">&ldquo;{review.wouldRecommend}&rdquo;</p>
                  </div>
                )}

                {/* Highlight Experience */}
                {review.highlightExperience && (
                  <div>
                    <div className="flex items-center mb-2">
                      <Star className="h-4 w-4 text-green-500 mr-2" />
                      <span className="text-sm font-medium text-gray-700">Exceeded Expectations</span>
                    </div>
                    <p className="text-gray-800 leading-relaxed">{review.highlightExperience}</p>
                  </div>
                )}

                {/* Additional Comments */}
                {review.additionalComments && (
                  <div>
                    <p className="text-gray-800 leading-relaxed">{review.additionalComments}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Show More/Less Button */}
        {reviews.length > 3 && (
          <div className="text-center mt-8">
            <button
              onClick={() => setShowAllReviews(!showAllReviews)}
              className="inline-flex items-center px-4 py-2 text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              {showAllReviews ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-2" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-2" />
                  Show All {reviews.length} Reviews
                </>
              )}
            </button>
          </div>
        )}
      </div>

    </section>
  );
};

export default JourneyReviewsSection;
